import type { TFunction } from 'i18next';

import { parseApiError } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';
import type { BulkAction } from '@saas-kit/ui/DataTable';

import { reactivateUser, suspendUser, type AdminUser } from './api';

interface Deps {
  t: TFunction;
  invalidate: () => Promise<void>;
}

async function runBulk(
  rows: AdminUser[],
  fn: (id: string) => Promise<AdminUser>,
  invalidate: () => Promise<void>,
  successMessage: string,
): Promise<void> {
  if (rows.length === 0) return;
  try {
    await Promise.all(rows.map((r) => fn(r.id)));
    await invalidate();
    toast.success(successMessage);
  } catch (err) {
    toast.error(parseApiError(err).message);
  }
}

export function buildUserBulkActions({ t, invalidate }: Deps): BulkAction<AdminUser>[] {
  return [
    {
      id: 'suspend',
      label: t('actions.suspend'),
      variant: 'destructive',
      isDisabled: (rows) =>
        rows.some((r) => r.is_active) ? null : t('bulk.no_active'),
      onRun: (rows) => {
        const targets = rows.filter((r) => r.is_active);
        return runBulk(
          targets,
          suspendUser,
          invalidate,
          t('toasts.bulk_suspended', { count: targets.length }),
        );
      },
    },
    {
      id: 'reactivate',
      label: t('actions.reactivate'),
      variant: 'secondary',
      isDisabled: (rows) =>
        rows.some((r) => !r.is_active) ? null : t('bulk.no_suspended'),
      onRun: (rows) => {
        const targets = rows.filter((r) => !r.is_active);
        return runBulk(
          targets,
          reactivateUser,
          invalidate,
          t('toasts.bulk_reactivated', { count: targets.length }),
        );
      },
    },
  ];
}
